<script setup lang="ts">
import { businessConfig } from '@/config';
import { accountService } from '@/services/accounts/accounts-service';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { ataService } from '@/services/ata/ata-service';
import { utils, type models, type Nullish } from 'cesg-shared';
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import VuePdfEmbed from 'vue-pdf-embed';
import { useRoute } from 'vue-router';

const route = useRoute();
const { t } = useI18n();
let displayFirefoxWarning = false;

const showData = ref<boolean>(false);

const documentUrl = ref<string | null>(null);

const PRINT_FALLBACK_MS = 10_000;
let hasPrinted = false;
let printFallbackTimer: ReturnType<typeof setTimeout> | undefined;

function revokeDocumentUrl(): void {
  if (documentUrl.value) {
    URL.revokeObjectURL(documentUrl.value);
    documentUrl.value = null;
  }
}

function printOnce(): void {
  if (hasPrinted) return;
  hasPrinted = true;
  clearTimeout(printFallbackTimer);
  overlaySpinnerService.hide();
  setTimeout(() => window.print(), 200);
}

function onDocumentFailure(error: Error): void {
  console.error(error);
  revokeDocumentUrl();
  printOnce();
}

const printFooterStyleId = 'ata-print-footer';

function escapeCssString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function buildPrintFooter(): string {
  const { ab, organizationNumber, website, address } = businessConfig.cesg;

  const rows = [
    [ab, organizationNumber],
    [
      `${address.zip} ${address.city}`,
      `${address.street} ${address.streetNumber}`,
      website,
    ],
  ];

  return rows.map((row) => row.map(escapeCssString).join(' | ')).join(' \\A ');
}

function addPrintFooterStyle(): void {
  const style = document.createElement('style');
  style.id = printFooterStyleId;
  style.textContent = `
    @media print {
      @page {
        @bottom-left {
          content: '${buildPrintFooter()}';
          white-space: pre;
          color: #001c74;
          font-size: 0.65rem;
          font-family: var(--font-primary);
        }
      }
    }
  `;
  document.head.appendChild(style);
}

function removePrintFooterStyle(): void {
  document.getElementById(printFooterStyleId)?.remove();
}

interface AtaFormData {
  projectId: string;
  id: Nullish<string>;
  title: Nullish<string>;
  status: Nullish<models.domain.AtaStatusType>;
  serialNumber: Nullish<number>;
  description: Nullish<string>;
  shouldBeRegulated: Nullish<models.YesNo>;
  leadsToTimeExtension: Nullish<models.YesNo>;
  pricingModel: Nullish<models.domain.AtaPricingModel>;
  shouldBeForecasted: Nullish<models.YesNo>;
  type: Nullish<models.domain.AtaType>;
  priority: Nullish<number>;
  sentDate: Nullish<string>;
  deadlineDate: Nullish<string>;
  revisionDate: Nullish<string>;
  answerReceivedDate: Nullish<string>;
  answer: Nullish<string>;
  costs: Array<models.AtaCost>;
}

const formData = ref<AtaFormData>({
  projectId: utils.type.asType<string>(route.params.id),
  id: null,
  title: null,
  status: null,
  serialNumber: null,
  description: null,
  shouldBeRegulated: null,
  leadsToTimeExtension: null,
  pricingModel: null,
  shouldBeForecasted: null,
  type: null,
  priority: null,
  sentDate: null,
  deadlineDate: null,
  revisionDate: null,
  answerReceivedDate: null,
  answer: null,
  costs: [],
});

const accounts = ref<{ number: number; name: string }[]>([]);

function mapGetAtaResponseToFormData(
  response: models.GetAtaResponse,
): AtaFormData {
  return {
    projectId: response.projectId,
    id: response.id ?? '',
    title: response.title ?? null,
    status: response.status ?? null,
    serialNumber: response.serialNumber ?? null,
    description: response.description ?? null,
    shouldBeRegulated: response.shouldBeRegulated ?? null,
    leadsToTimeExtension: response.leadsToTimeExtension ?? null,
    pricingModel: response.pricingModel ?? null,
    shouldBeForecasted: response.shouldBeForecasted ?? null,
    type: response.type ?? null,
    priority: response.priority ?? null,
    sentDate: response.sentDate ?? null,
    deadlineDate: response.deadlineDate ?? null,
    answerReceivedDate: response.answerReceivedDate ?? null,
    revisionDate: response.revisionDate ?? null,
    answer: response.answer ?? null,
    costs: response.costs ?? [],
  };
}

onMounted(async () => {
  if (navigator.userAgent.includes('Firefox')) {
    displayFirefoxWarning = true;
  }
  addPrintFooterStyle();
  overlaySpinnerService.show();

  const documentPromise =
    route.params.ataId === '0'
      ? Promise.resolve(null)
      : ataService
          .getDocument(
            utils.type.asType<string>(route.params.id),
            utils.type.asType<string>(route.params.ataId),
          )
          .catch((error) => {
            console.error(error);
            return null;
          });

  try {
    const response = await ataService.get(
      utils.type.asType<string>(route.params.ataId),
    );

    accounts.value = await accountService.getAll();
    formData.value = mapGetAtaResponseToFormData(response);
  } catch (error) {
    console.error(error);
  }

  const documentBlob = await documentPromise;
  if (documentBlob) documentUrl.value = URL.createObjectURL(documentBlob);

  showData.value = true;

  if (!documentUrl.value) {
    printOnce();
    return;
  }

  printFallbackTimer = setTimeout(printOnce, PRINT_FALLBACK_MS);
});

onUnmounted(() => {
  removePrintFooterStyle();
  clearTimeout(printFallbackTimer);
  revokeDocumentUrl();
});

function sumAmount(): number {
  let totalAmount = 0;
  formData.value.costs.forEach((cost) => {
    totalAmount += cost.amount;
  });

  return totalAmount;
}

function tranlateYesOrNo(input: Nullish<models.YesNo>): string {
  if (typeof input === 'string') {
    return t(input);
  }
  return ' ';
}

function tranlateStatus(input: Nullish<models.domain.AtaStatusType>): string {
  if (typeof input === 'string') {
    return t(input);
  }
  return ' ';
}
</script>

<template>
  <div v-if="displayFirefoxWarning" class="columns browser-warning">
    <div class="column has-text-centered">
      <h1 class="title is-1 has-text-danger">
        Varning, Firefox stöttar inte alla funktioner för att få en bra utskrift
      </h1>
    </div>
  </div>

  <div class="columns">
    <div class="column has-text-left">
      <h1 class="title is-4">{{ formData.title }}</h1>
      <p class="subtitle is-6 mt-1">
        {{ t('ata.table.status') }}: {{ tranlateStatus(formData.status) }}
      </p>
      <p v-if="formData.revisionDate" class="md-3">
        {{ t('ata.details.lastRevision') }}: {{ formData.revisionDate }}
      </p>
    </div>
  </div>
  <div class="page-indent">
    <div class="columns is-centered mt-6">
      <div class="column is-centered is-one-third">
        <p>
          <b>{{ t('ata.details.form.sentDate') }}</b>
        </p>
        <p>{{ formData.sentDate }}</p>
      </div>
      <div class="column is-centered is-one-third">
        <p>
          <b>{{ t('ata.details.form.deadlineDate') }}</b>
        </p>
        <p>{{ formData.deadlineDate }}</p>
      </div>
      <div class="column is-centered is-one-third">
        <p>
          <b>{{ t('ata.details.form.answerReceivedDate') }}</b>
        </p>
        <p>{{ formData.answerReceivedDate }}</p>
      </div>
    </div>

    <hr />

    <div class="columns is-centered">
      <div class="column is-half">
        <p>
          <b>{{ t('ata.details.form.description') }}</b>
        </p>
        <p>{{ formData.description }}</p>
      </div>
      <div class="column is-half">
        <p>
          <b>{{ t('ata.details.form.answer') }}</b>
        </p>
        <p>{{ formData.answer }}</p>
      </div>
    </div>

    <div class="columns is-centered mt-6">
      <div class="column is-half">
        <p>
          <b>{{ t('ata.details.form.shouldBeRegulated') }}</b>
        </p>
        <p>{{ tranlateYesOrNo(formData.shouldBeRegulated) }}</p>
      </div>
      <div class="column is-half">
        <p>
          <b>{{ t('ata.details.form.pricingModel') }}</b>
        </p>
        <p>{{ formData.pricingModel }}</p>
      </div>
    </div>

    <div class="columns is-centered mt-3">
      <div class="column is-half">
        <p>
          <b>{{ t('ata.details.form.leadsToTimeExtension') }}</b>
        </p>
        <p>{{ tranlateYesOrNo(formData.leadsToTimeExtension) }}</p>
      </div>
      <div class="column is-half">
        <p>
          <b>{{ t('ata.details.form.type') }}</b>
        </p>
        <p>{{ formData.type }}</p>
      </div>
    </div>

    <div class="columns mt-6">
      <table class="table is-fullwidth is-striped">
        <thead>
          <tr>
            <th>
              {{ t('ata.details.costs.accountNumber') }}
            </th>
            <th>
              {{ t('ata.details.costs.description') }}
            </th>
            <th>
              {{ t('ata.details.costs.numberOfItems') }}
            </th>
            <th>
              {{ t('ata.details.costs.unit') }}
            </th>
            <th>
              {{ t('ata.details.costs.pricePerUnit') }}
            </th>
            <th>
              {{ t('ata.details.costs.markupFactor') }}
            </th>
            <th>
              {{ t('ata.details.costs.amount') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cost in formData.costs" :key="cost.id + 'cost'">
            <td :key="cost.id + 'accountNumber'">
              {{ cost.accountNumber }} -
              {{
                accounts.find(({ number }) => number === cost.accountNumber)
                  ?.name
              }}
            </td>
            <td :key="cost.id + 'description'">
              {{ cost.description }}
            </td>
            <td :key="cost.id + 'numberOfItems'">
              {{ utils.formatNumber(cost.numberOfItems) }}
            </td>
            <td :key="cost.id + 'unit'">
              {{ cost.unit }}
            </td>
            <td :key="cost.id + 'pricePerUnit'">
              {{ utils.formatNumber(cost.pricePerUnit) }}
            </td>
            <td :key="cost.id + 'markupFactor'">
              {{ utils.formatNumberWithDecimal(cost.markupFactor, 1) }}%
            </td>
            <td :key="cost.id + 'amount'">
              {{ utils.formatNumber(cost.amount) }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>
              {{ t('sum') }}
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td>
              {{ utils.formatNumber(sumAmount()) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <section v-if="documentUrl" class="ata-print-document">
      <h2 class="title is-5">{{ t('ata.details.cards.ataDocuments') }}</h2>
      <VuePdfEmbed
        :source="documentUrl"
        :width="1000"
        :scale="2"
        @rendered="printOnce"
        @loading-failed="onDocumentFailure"
        @rendering-failed="onDocumentFailure"
      />
    </section>
  </div>
</template>

<style lang="scss" scoped>
@use 'bulma/sass/utilities/initial-variables' as *;

.columns {
  margin: 0 -0.25rem !important;
  page-break-inside: avoid !important;
}

.title {
  margin-bottom: 1px;
}

.subtitle {
  margin-bottom: 1px;
}

.page-indent {
  margin-left: 15px;
}

.footer-print {
  bottom: 0px;
}

.ata-print-document .title {
  margin-bottom: 0.75rem;
}

:deep(.vue-pdf-embed canvas) {
  width: auto !important;
  height: auto !important;
  max-width: 100%;
}

@media print {
  .ata-print-document {
    break-before: page;
  }

  .ata-print-document .title {
    break-after: avoid;
  }

  :deep(.vue-pdf-embed canvas) {
    max-height: 230mm;
  }

  :deep(.vue-pdf-embed > div:not(:first-child)) {
    break-before: page;
  }
}
</style>

<style lang="scss">
@use '@/assets/style/variables';
@use 'bulma/sass/utilities/initial-variables' as *;

@media print {
  @page {
    margin-top: 20mm;
    margin-bottom: 20mm;

    @top-right {
      content: 'Sida ' counter(page) ' av ' counter(pages);
      font-family: var(--font-primary);
      font-size: 0.85rem;
    }

    @top-left {
      content: url('/src/assets/logo/CESG.svg');
      width: 100px;
      height: auto;
    }
  }

  thead {
    tr th {
      font-size: 0.7rem;
    }
  }
  tbody {
    tr td {
      font-size: 0.65rem;
    }
  }
  tfoot {
    tr td {
      font-size: 0.7rem;
    }
  }

  .columns {
    font-size: 0.9rem;
    break-inside: avoid !important;
  }

  .browser-warning {
    display: none !important;
  }

  .footer {
    display: none !important;
  }

  .tabs {
    display: none !important;
  }

  header {
    display: none !important;
  }

  hr {
    display: block;
    height: 1px;
    background: transparent;
    width: 100%;
    border: none;
    border-top: solid 2px #d6d9e0;
  }
}
</style>
